import React, { Fragment } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import PropTypes from 'prop-types';
import { createBrowserHistory } from 'history';
import { Provider } from 'react-redux';

import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';

import compose from 'recompose/compose';
import withState from 'recompose/withState';
import withHandlers from 'recompose/withHandlers';
import lifecycle from 'recompose/lifecycle';
import pure from 'recompose/pure';

import CssBaseline from '@material-ui/core/CssBaseline';
import createTheme from '@material-ui/core/styles/createTheme';
import { ThemeProvider } from '@material-ui/styles';
import 'typeface-roboto';

import Loadable from 'react-loadable';

import GoogleTagManager from './components/GoogleTagManager/GoogleTagManager';
import getConfiguration from './api/Configuration';
import {
    getValidLang,
    getLocalLanguage,
    saveLocalLanguage,
    saveDefaultFleetLanguage,
    getLabelsForLanguage,
    getAvailableLocales,
} from './api/Locale';
import OpenIDClient from './api/OpenIDClient';
import { initAuth, getState as getAuthState, subscribeAuthEvents } from './auth/authClient';
import UserClient from './api/UserClient';
import BookingClient from './api/BookingClient';
import StationClient from './api/StationClient';
import VehicleClient from './api/VehicleClient';

import Loading from './components/Popup/Loading';
import { ConfigurationProvider } from './components/Contexts/Configuration';
import { AclProvider } from './components/Contexts/Acl';
import { LocaleProvider, initSetLanguage } from './components/Contexts/Locale';

import configureStore from './redux/configureStore';
import sagas from './sagas';
import { bridgeSsoSettled, loginSetAuthenticate, handleAzureRedirect } from './redux/login/actions';
import { setApiConfig, setDefaultLocale, initApp } from './redux/app/actions';
import { getDevHost, isHostSwitcherMounted, mountDeployedHostPill } from './helpers/hostSwitcher';
import { initBugLogger, logError } from './helpers/bugLogger';

import './importAllLocales';
import TripsClient from './api/TripsClient';

const LoadingComponent = ({ pastDelay }) => (pastDelay ? <Loading /> : null);

LoadingComponent.propTypes = {
    pastDelay: PropTypes.bool,
};

const Root = Loadable({
    loader: () => import(/* webpackChunkName: "Root" */ './components/Root'),
    loading: LoadingComponent,
    modules: ['Root'],
});

const StartApp = Loadable({
    loader: () => import(/* webpackChunkName: "StartApp" */ './components/Popup/StartApp'),
    loading: LoadingComponent,
    modules: ['StartApp'],
});

const enhance = compose(
    withState('isLoading', 'setLoading', true),
    withState('client', 'setClient', null),
    withState(
        'theme',
        'setTheme',
        createTheme({
            typography: {
                useNextVariants: true,
            },
            overrides: {
                MuiCssBaseline: {
                    '@global': {
                        'html, body, #root': {
                            height: '100vh',
                            margin: 0,
                        },
                    },
                },
            },
        })
    ),
    withState('config', 'setConfig', {}),
    withState('locale', 'setLocale', {}),
    withState('language', 'setLanguage', ''),
    withState('store', 'setStore', null),
    withState('availableLanguages', 'setAvailableLanguages', []),
    withHandlers({
        changeFavicon: () => src => {
            const href = `${src}?v=${Math.random()}`; // for avoiding cache the image
            let link =
                document.querySelector('link[rel="shortcut icon"]') || document.querySelector('link[rel="icon"]');

            if (!link) {
                link = document.createElement('link');
                link.id = 'favicon';
                link.rel = 'shortcut icon';
                document.head.appendChild(link);
            }

            link.href = href;
        },
        loadFaviconImage: () => (imageSrc, good, bad) => {
            const img = new Image();
            img.onload = good;
            img.onerror = bad;
            img.src = imageSrc;
        },
    }),
    withHandlers({
        loadGoogleFont: () => fontFamily => {
            const fontToDownload = fontFamily
                .split(',')[0]
                .trim()
                .split(' ')
                .join('+');
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css?family=${fontToDownload}`;
            document.head.appendChild(link);
        },
    }),
    withHandlers({
        onSelectedClient: ({
            setClient,
            setTheme,
            setLoading,
            setConfig,
            setLocale,
            setAvailableLanguages,
            setLanguage,
            setStore,
            changeFavicon,
            loadFaviconImage,
            loadGoogleFont,
        }) => async client => {
            setClient(client);

            const { themes, ...config } = await getConfiguration(client);
            mountDeployedHostPill(client, config.api.baseEndpoint);

            const language = getLocalLanguage() || config.api.defaultLang;
            let alternateTranslations = [];
            try {
                alternateTranslations = JSON.parse(config.api.alternateTranslations.overrides);
            } catch (e) {
                console.error('Error parsing alternate translations', e);
            }
            const localization = await getLabelsForLanguage(language, alternateTranslations);
            const allAvailableLanguages = await getAvailableLocales();
            const availableLanguages = allAvailableLanguages
                .filter(key => config.api.availableLanguages.includes(key.lang_iso))
                .map(key => ({ name: key.lang_name, code: key.lang_iso }));
            document.title = config.api.title;

            const { store, run } = configureStore();

            // Setting stored API configuration
            if (config) {
                // eslint-disable-next-line no-unused-vars
                const { anonymousAccess, authEndpoint, authenticatedAccess, baseEndpoint, ...rest } = config.api;
                store.dispatch(
                    setApiConfig({
                        anonymousAccess,
                        authEndpoint,
                        authenticatedAccess,
                        baseEndpoint,
                    })
                );
            }

            // Search for "rfu" and "token_refresh" in the URL with query parameters
            const urlParams = new URLSearchParams(window.location.search);
            const rfu = urlParams.get('rfu');
            const redirectionFromMicrosoft = rfu && rfu.includes('https') && rfu.includes('token_refresh');

            // We'll store this for later, AFTER sagas are initialized
            const shouldHandleAzureRedirect = redirectionFromMicrosoft;

            // Authentication methods will be fetched via discoverAuthMethods in the Azure auth saga
            // This ensures we have a single source of truth for the Azure button visibility

            OpenIDClient.initOpenID(config.api);
            UserClient.init(config.api);
            BookingClient.init(config.api);
            StationClient.init(config.api);
            VehicleClient.init(config.api);
            TripsClient.init(config.api);

            // Bug Logger: initialise from the tenant config. The ingestion
            // target is resolved by resolveEndpoint (see helpers/bugLogger.js)
            // from the RUNTIME tenant env only — 'PROD' reaches the prod project,
            // everything else the dev one. It does not depend on the build type;
            // the only build-related gate is NODE_ENV === 'development' (the local
            // dev server), which skips init entirely. resolveStage separately
            // computes the releaseStage label. Fire-and-forget; failures are
            // swallowed inside initBugLogger so they never block the app.
            initBugLogger({
                env: config.env || (config.api && config.api.env),
                version: process.env.REACT_APP_VERSION,
                client,
            });

            // The @vulog/auth Service Worker now owns the tokens. Push the tenant
            // config to it, then read the (token-free) session state; getState()
            // transparently refreshes an expired-but-refreshable session, so no
            // explicit boot-time refresh is needed here.
            // Bridge the SW session-loss broadcast to Redux: PrivateRoute reads
            // the static `isAuthenticate` flag, so without this a silent refresh
            // failure would leave protected routes rendering while every API call
            // 401s. Flip the flag so the guard redirects.
            //
            // Attached BEFORE initAuth and outside its try: it needs no client, and
            // a failed init is precisely when this guard matters most. Registering
            // it after the await would drop it in that case, and miss any broadcast
            // arriving during init.
            subscribeAuthEvents(event => {
                if (!event || event.type !== 'AUTH_EVT_INVALID') return;
                store.dispatch(loginSetAuthenticate(false));
                const reason = (event.payload && event.payload.reason) || 'unknown';
                // `logout` is the user asking for this. Anything else is a session
                // the app LOST mid-use — the user is about to be bounced to the
                // login screen with no explanation, and without this nobody ever
                // learns it happened. The commonest cause is an IDP that is not
                // same-site with the app, so the worker's background refresh
                // cannot carry the SSO cookie.
                if (reason === 'logout') return;
                console.error('Auth session ended unexpectedly:', reason);
                logError(new Error(`Auth session ended unexpectedly: ${reason}`));
            });

            let isAuthed = false;
            try {
                await initAuth(config.api);
                const authState = await getAuthState();
                isAuthed = authState.phase === 'authed';
            } catch (e) {
                // Degrade to an anonymous-but-usable app, but keep the error
                // visible: a systemic misconfiguration (bad realm URL for a whole
                // tenant) otherwise surfaces only as "everyone is anonymous". The
                // console is invisible in a OneApp webview, so report it too.
                console.error('Auth Service Worker init failed', e);
                logError(e instanceof Error ? e : new Error(String(e)));
            }

            store.dispatch(loginSetAuthenticate(isAuthed));
            store.dispatch(setDefaultLocale(config.api.defaultLocale));

            initSetLanguage(lgSelected => {
                const lg = getValidLang(lgSelected, config.api.availableLanguages, config.api.defaultLang);
                setLanguage(lg);
                saveLocalLanguage(lg);
                saveDefaultFleetLanguage(config.api.defaultLang);
                moment.locale(lg.toLowerCase().replace('_', '-'));
            });

            // Initialize saga middleware
            run(sagas);

            if (!isAuthed) {
                // Initialize the application including Azure authentication
                store.dispatch(initApp());
            } else {
                // APP_INIT is what runs the SSO bridge saga, so an already
                // authenticated arrival would leave its render hold armed for
                // the life of the document — harmless while authenticated, and
                // a ten-second blank page the moment anything sets auth back to
                // false (a lost session, a failed hydration).
                store.dispatch(bridgeSsoSettled());
            }

            // Handle Azure redirect if we were redirected from Microsoft
            // Only handle redirect if user is not authenticated (fresh redirect, not after logout)
            if (shouldHandleAzureRedirect && !isAuthed) {
                store.dispatch(handleAzureRedirect());
            }

            setStore(store);
            setConfig({ ...config, api: { ...config.api, iconModele: themes.iconModele } });
            setAvailableLanguages(availableLanguages);
            setLocale(localization.locale);
            const lg = getValidLang(getLocalLanguage(), config.api.availableLanguages, config.api.defaultLang);
            setLanguage(lg);
            saveLocalLanguage(lg);
            saveDefaultFleetLanguage(config.api.defaultLang);
            moment.locale(lg.toLowerCase().replace('_', '-'));
            if (themes.typography.fontFamily) {
                loadGoogleFont(themes.typography.fontFamily);
            }
            setTheme(
                createTheme({
                    ...themes,
                    overrides: {
                        ...themes.overrides,
                        MuiCssBaseline: {
                            '@global': {
                                'html, body, #root': {
                                    height: '100vh',
                                    margin: 0,
                                },
                            },
                        },
                    },
                })
            );
            if (themes.favicon) {
                const iconPath = themes.favicon;
                loadFaviconImage(
                    iconPath,
                    () => changeFavicon(iconPath),
                    () => changeFavicon('/favicon.png')
                );
            }
            setLoading(false);
        },
        onSelectedLanguage: ({ config, setLanguage, setLocale }) => async language => {
            const lg = getValidLang(language, config.api.availableLanguages, config.api.defaultLang);
            const alternateTranslations = JSON.parse(config.api.alternateTranslations.overrides) || [];
            const localization = await getLabelsForLanguage(lg, alternateTranslations);
            setLocale(localization.locale);
            setLanguage(lg);
            saveLocalLanguage(lg);
            moment.locale(lg.toLowerCase().replace('_', '-'));
        },
    }),
    lifecycle({
        componentDidMount() {
            const isDevelopment = process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'development';
            if (!isDevelopment) {
                const {
                    location: { hostname },
                } = window;
                this.props.onSelectedClient(hostname);
                return;
            }
            const devHost = getDevHost();
            if (devHost) {
                this.props.onSelectedClient(devHost).catch(error => console.warn('Host conf failed to load', error));
            }
        },
    }),
    pure
);

const history = createBrowserHistory();

const App = ({
    isLoading,
    theme,
    onSelectedClient,
    client,
    config,
    locale,
    language,
    onSelectedLanguage,
    availableLanguages,
    store,
}) => {
    return (
        <>
            <ThemeProvider theme={theme}>
                <Fragment>
                    <CssBaseline />
                    {isLoading ? (
                        <StartApp
                            isChooseClient={!client && !isHostSwitcherMounted()}
                            onSelectedClient={onSelectedClient}
                        />
                    ) : (
                        <ConfigurationProvider {...config}>
                            <AclProvider acl={config.acl}>
                                <LocaleProvider
                                    language={language}
                                    l10n={locale}
                                    onSelectedLanguage={onSelectedLanguage}
                                    availableLanguages={availableLanguages}
                                >
                                    <Provider store={store}>
                                        <MuiPickersUtilsProvider
                                            utils={MomentUtils}
                                            libInstance={moment}
                                            locale={moment.locale()}
                                        >
                                            <Router history={history}>
                                                <Root />
                                            </Router>
                                        </MuiPickersUtilsProvider>
                                    </Provider>
                                </LocaleProvider>
                            </AclProvider>
                            <GoogleTagManager gtmId={config.googleTagManagerId} />
                        </ConfigurationProvider>
                    )}
                </Fragment>
            </ThemeProvider>
        </>
    );
};

App.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    theme: PropTypes.shape({}).isRequired,
    onSelectedClient: PropTypes.func.isRequired,
    client: PropTypes.string,
    config: PropTypes.shape({
        acl: PropTypes.shape({}),
        googleTagManagerId: PropTypes.string,
    }).isRequired,
    store: PropTypes.shape({}),
    locale: PropTypes.shape({}),
    availableLanguages: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    language: PropTypes.string,
    onSelectedLanguage: PropTypes.func.isRequired,
};

export default enhance(App);
